import { Button, Tooltip, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import JSZip from 'jszip';
import * as _ from 'lodash';
import React, { useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { LenderShareReportFragmentGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { fileExtensionToMimeTypeMap } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useGetLenderShareReportsForLoanQuery } from 'packages/loans/requests/loans.gql.generated';
import { useGetReportsS3DownloadUrlsLazyQueryGQLType } from 'packages/reporting/requests/reporting.gql.generated';

// returns the correct file extension for a Blob which is required for files opening correctly when unzipping
const getFileExtensionFromBlob = (blob: Blob): string | undefined => {
  return Object.entries(fileExtensionToMimeTypeMap).find(
    ([_extension, mimeType]) => mimeType === blob.type,
  )?.[0];
};

const DownloadButtonBody = ({
  reports,
  loanId,
}: {
  reports: LenderShareReportFragmentGQLType[];
  loanId: string;
}) => {
  const toast = useToast();

  const todayDateStr = commonDate.currentDateStr();

  const [
    getReportsUrlsQuery,
    { loading: isGetReportsUrlsLoading },
  ] = useGetReportsS3DownloadUrlsLazyQueryGQLType({
    fetchPolicy: 'network-only',
    variables: {
      reportIdsWithFilename: reports.map((report) => ({
        reportId: report.id,
        filename: `funding-notice-${todayDateStr}${
          report.capitalProviderName
            ? `-${_.kebabCase(report.capitalProviderName)}`
            : ''
        }`,
      })),
      reportConfigType: 'fundingNotice',
    },
    onCompleted: async ({ getReportsS3DownloadUrls }) => {
      if (!getReportsS3DownloadUrls) {
        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URLs for funding notices`,
        });
        return;
      }

      try {
        const zip = new JSZip();

        await Promise.all(
          getReportsS3DownloadUrls.map(async (downloadUrl, index) => {
            const response = await axios({
              url: downloadUrl.s3DownloadUrl,
              method: 'GET',
              responseType: 'blob',
            });

            const fileBlob = new Blob([response.data], {
              type: response.data.type,
            });
            const singleFileName = downloadUrl.filename;
            const singleFileExtension = getFileExtensionFromBlob(fileBlob);

            const fileNameToZip = singleFileExtension
              ? `${index + 1}-${singleFileName}.${singleFileExtension}`
              : `${index + 1}-${singleFileName}`;

            zip.file(fileNameToZip, fileBlob);
          }),
        );

        const zippedFileBlob = await zip.generateAsync({ type: 'blob' });
        const zippedFileName = `funding-notices-${todayDateStr}`;

        FileSaver.saveAs(zippedFileBlob, zippedFileName);
      } catch (err) {
        Sentry.captureException(
          `Error when zipping and downloading funding notices for [loanId: ${loanId}]: ${err}`,
        );

        toast({
          ...errorToastConfig,
          title: `Failed to download funding notices`,
        });
      }
    },
  });

  return (
    <Tooltip label="Download all notices for loan">
      <Button
        variant="unstyled"
        onClick={() => getReportsUrlsQuery()}
        fontWeight={400}
        isLoading={isGetReportsUrlsLoading}
      >
        Download All Funding Notices
      </Button>
    </Tooltip>
  );
};

type DownloadAllLiveFundingNoticesButtonBodyParams = {
  loanId: string;
  fundingRequestId: string;
};
const DownloadAllLiveFundingNoticesButtonBody = ({
  loanId,
  fundingRequestId,
}: DownloadAllLiveFundingNoticesButtonBodyParams) => {
  const {
    data: lenderShareReportsDownloadUrlsData,
    loading: isLenderShareReportsDownloadUrlsLoading,
  } = useGetLenderShareReportsForLoanQuery({
    variables: { loanId, fundingRequestId },
    fetchPolicy: 'no-cache',
  });

  if (
    isLenderShareReportsDownloadUrlsLoading ||
    !lenderShareReportsDownloadUrlsData
  ) {
    return (
      <Button variant="unstyled" isLoading>
        Download All Funding Notices
      </Button>
    );
  }

  const fundingNoticeReports = lenderShareReportsDownloadUrlsData.reports.filter(
    (report) => report.reportType === 'fundingNotice',
  );

  if (!fundingNoticeReports.length) {
    return (
      <Tooltip label="Funding notices are not found">
        <Button isDisabled variant="v2-ghost" fontWeight={400}>
          Download All Funding Notices
        </Button>
      </Tooltip>
    );
  }

  return <DownloadButtonBody reports={fundingNoticeReports} loanId={loanId} />;
};

type DownloadAllLiveFundingNoticesButtonParams = {
  loanId: string;
  creditFacilityId: string;
  fundingRequestId: string;
};
export const DownloadAllLiveFundingNoticesButton = ({
  loanId,
  creditFacilityId,
  fundingRequestId,
}: DownloadAllLiveFundingNoticesButtonParams) => {
  const toast = useToast();
  const {
    data: creditAgreementData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    onError: () => {
      errorToastConfig.title =
        'An unexpected error occurred when fetching the current credit agreement. Please contact Finley for support.';

      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  const creditAgreement = useMemo(
    () =>
      parseCreditAgreementFullArtifact(
        creditAgreementData?.currentCreditAgreementFullArtifact
          ?.creditAgreement,
      ),
    [creditAgreementData],
  );

  const reportName = useMemo(() => {
    const reportingProperty = creditAgreement?.reportingMechanics.find(
      (reportingMechanic) =>
        reportingMechanic.reportConfigTypes.includes('fundingNotice'),
    );
    return reportingProperty?.name;
  }, [creditAgreement?.reportingMechanics]);

  if (!creditAgreement) {
    return <Button variant="v2-ghost" isLoading />;
  }

  if (!reportName) {
    return (
      <Tooltip label="Funding notices are not configured">
        <Button isDisabled variant="v2-ghost">
          Download All Funding Notices
        </Button>
      </Tooltip>
    );
  }

  return (
    <DownloadAllLiveFundingNoticesButtonBody
      loanId={loanId}
      fundingRequestId={fundingRequestId}
    />
  );
};
